/**
 * PATH: packages/quote-builder/src/branding/types.ts
 * PURPOSE: API-driven quote branding contract — replaces the three
 *          hardcoded per-app quoteBranding.ts constants (SAM, Newmark,
 *          Bravada) with a payload sourced from OrganisationSettings /
 *          OrganisationBranding (ADR-0002 runtime branding model).
 *
 * Apps map their backend payload through `brandFromSettings` and keep
 * their current hardcoded constant ONLY as the documented fallback for
 * half-configured orgs.
 */

import { ACCENT_DARK_FACTOR, clampLogoDisplayHeightPx, shadeHex } from './defaults'

export interface QuoteBrand {
  companyName: string
  abn?: string
  tagline?: string
  /** Primary accent (hex) — the org's configured brand colour. */
  accent: string
  accentDark?: string
  secondary?: string
  ink?: string
  muted?: string
  hairline?: string
  tint?: string
  logoUrl?: string | null
  /** Inline data: URI logo for public/print surfaces that can't send the
   *  auth header logoUrl's /api/files/ route needs. */
  logoDataUri?: string | null
  /** Max org logo height in CSS pixels on quote PDF / ITP. Default 46. */
  logoDisplayHeightPx?: number
  address?: string
  phone?: string
}

/** Shape of the backend branding payload (OrganisationSettings/Branding). */
export interface OrganisationBrandingPayload {
  company_name?: string | null
  abn?: string | null
  tagline?: string | null
  logo_url?: string | null
  logo_data_uri?: string | null
  logo_display_height_px?: number | null
  brand_color_primary?: string | null
  brand_color_secondary?: string | null
  address?: string | null
  phone?: string | null
}

/**
 * Merge an org branding payload over a fallback brand.
 * Empty/null API values fall through to the fallback so a half-configured
 * org never renders an unbranded quote. A configured accent re-derives
 * accentDark (same shade rule as the backend PDF twin) so the pair can
 * never mix two orgs' colours.
 */
export function brandFromSettings(
  payload: OrganisationBrandingPayload | null | undefined,
  fallback: QuoteBrand,
): QuoteBrand {
  if (!payload) return fallback
  const accent = payload.brand_color_primary?.trim() || fallback.accent
  return {
    ...fallback,
    companyName: payload.company_name?.trim() || fallback.companyName,
    abn: payload.abn?.trim() || fallback.abn,
    address: payload.address?.trim() || fallback.address,
    phone: payload.phone?.trim() || fallback.phone,
    tagline: payload.tagline?.trim() || fallback.tagline,
    accent,
    accentDark: payload.brand_color_primary?.trim()
      ? shadeHex(accent, ACCENT_DARK_FACTOR)
      : fallback.accentDark,
    secondary: payload.brand_color_secondary?.trim() || fallback.secondary,
    logoUrl: payload.logo_url?.trim() || fallback.logoUrl,
    logoDataUri: payload.logo_data_uri?.trim() || fallback.logoDataUri,
    logoDisplayHeightPx:
      payload.logo_display_height_px == null
        ? fallback.logoDisplayHeightPx ?? clampLogoDisplayHeightPx(undefined)
        : clampLogoDisplayHeightPx(payload.logo_display_height_px),
  }
}
